import { TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { NumericFormat as NumberFormat, NumberFormatValues, PatternFormat } from 'react-number-format';
import logger from 'utils/logger';

const useStyles = makeStyles((theme: any) => ({
  input: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
    },
    '& .MuiOutlinedInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
    },
    '& .Mui-disabled': { cursor: 'not-allowed' }
  },
  naked: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
    '& .MuiFilledInput-input': {
      paddingTop: 10,
      paddingLeft: 0,
    },
    '& .MuiOutlinedInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
    '& .Mui-disabled': { cursor: 'not-allowed' }
  },
  title: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: 24,
      color: theme.palette.primary.darker,
      fontWeight: 'bold',
    },
  },
  icon: { padding: 0 },
}));

type NumberFieldProps = {
  customRef?: any;
  fullWidth?: boolean;
  name?: string;
  color?: 'primary' | 'secondary';
  variant?: 'filled' | 'outlined';
  naked?: boolean;
  isTitle?: boolean;
  disabled?: boolean;
  prefix?: string;
  decimalScale?: number | null;
  fixedDecimalScale?: boolean;
  isAllowed?: (values: NumberFormatValues) => boolean;
  thousandSeparator?: string | boolean;
  customInput?: React.ComponentType<any>;
  label?: string | React.ReactNode;
  placeholder?: string;
  value: number | string | null | undefined;
  error?: boolean;
  helperText?: string;
  handleBlur?: any;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onValueChange?: (values: NumberFormatValues, sourceInfo: { source: 'event' | 'prop' }) => void;
  InputLabelProps?: any
  InputProps?: any;
  isNumericString?: boolean,
  allowNegative?: boolean,
  suffix?: string,
  style?: React.CSSProperties | undefined,
  className?: string
  mask?: string
  // Pattern mask (e.g. `####`). Switches the field to PatternFormat, which formats a fixed pattern
  // and ignores the numeric-mode props (prefix, suffix, thousandSeparator, decimalScale,
  // allowNegative, fixedDecimalScale).
  format?: string
  autoComplete?: string
  type?: 'text' | 'tel' | 'password' | undefined,
  onFocus?: (e: any) => void
};

const noop = () => {};
const DEFAULT_IS_ALLOWED = ({ floatValue = 0 }) => floatValue >= -99_999_999_999_999.99 && floatValue <= 99_999_999_999_999.99;
let didWarnIgnoredPatternProps = false;

const NumberField = forwardRef<any, NumberFieldProps>(({
  customRef,
  fullWidth = true,
  name,
  color = 'primary',
  variant = 'filled',
  naked = false,
  isTitle = false,
  disabled = false,
  prefix,
  decimalScale = 2,
  fixedDecimalScale = false,
  isAllowed = DEFAULT_IS_ALLOWED,
  thousandSeparator,
  customInput = TextField,
  label,
  placeholder,
  value,
  error = false,
  helperText,
  handleBlur,
  onChange = noop,
  onValueChange,
  InputLabelProps,
  InputProps,
  isNumericString = false,
  allowNegative = true,
  suffix,
  style,
  className = '',
  mask,
  format,
  autoComplete,
  type,
  onFocus
}, ref) => {
  const classes = useStyles();

  const sharedProps = {
    inputRef: customRef || ref,
    fullWidth,
    variant,
    name,
    color,
    disabled,
    isAllowed,
    customInput,
    className: `${className} ${clsx({ [classes.input]: !naked, [classes.naked]: naked, [classes.title]: isTitle })}`,
    valueIsNumericString: isNumericString,
    label,
    placeholder,
    value: value !== null ? value : '',
    error,
    helperText,
    onBlur: handleBlur,
    onChange,
    InputProps: { ...InputProps },
    onValueChange,
    InputLabelProps,
    style,
    autoComplete,
    type,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(e);
      } else if (parseFloat(e.target.value) === 0) e.target.value = '';
    },
  };

  // react-number-format v5 split v4's NumberFormat: `format`/`mask` belong to PatternFormat only.
  if (format) {
    if (import.meta.env.DEV && !didWarnIgnoredPatternProps && (prefix || suffix || thousandSeparator)) {
      didWarnIgnoredPatternProps = true;
      logger.warn('[NumberField] `format` selects pattern mode and ignores prefix/suffix/thousandSeparator.');
    }
    return <PatternFormat {...sharedProps} format={format} mask={mask} />;
  }

  return (
    <NumberFormat
      {...sharedProps}
      prefix={prefix}
      decimalScale={decimalScale === null ? undefined : decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      thousandSeparator={thousandSeparator}
      allowNegative={allowNegative}
      suffix={suffix}
    />
  );
});

export default NumberField;
